import { createContext, useCallback, useEffect, useState, type ReactNode, useContext, useRef } from 'react';
import { useAutoPushSubscription } from '@/hooks/useAutoPushSubscription';
import {
  authClient,
  clearBackendUrl as clearBackendUrlFn,
  clearDeviceToken,
  getBackendUrl,
  getDeviceToken,
  setBackendUrl as setBackendUrlFn,
  setCachedSessionToken,
} from '@/lib/better-auth';
import { initNativePushNotifications } from '@/lib/native-push';
import { isNativePlatform } from '@/lib/native-features';
import { useNotificationWebSocket } from '@/lib/notifications';
import { useBackgroundWs, stopBackgroundService } from '@/lib/notifications/use-background-ws';

interface AuthUser {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isPending: boolean;
  backendUrl: string;
  setBackendUrl: (url: string) => void;
  clearBackendUrl: () => void;
  setNativeUser: (user: AuthUser, token: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const isNative = isNativePlatform();

  // On native, skip useSession entirely — it relies on cookies which don't persist.
  // Use device token (API key) for auth instead.
  const { data: session, isPending: isSessionPending } = isNative
    ? { data: null, isPending: false }
    : authClient.useSession();

  const sessionUser = (session?.user as AuthUser | undefined) ?? null;
  const sessionToken = (session?.session as { token?: string } | undefined)?.token ?? null;

  // Device token auth state (native only)
  const [deviceUser, setDeviceUser] = useState<AuthUser | null>(null);
  const [isDevicePending, setIsDevicePending] = useState(isNative);

  const fetchDeviceSession = useCallback(async () => {
    const deviceToken = getDeviceToken();
    if (!deviceToken) {
      setIsDevicePending(false);
      return;
    }

    setIsDevicePending(true);
    try {
      const baseUrl = getBackendUrl();
      const res = await fetch(`${baseUrl}/api-keys/device-session`, {
        headers: { Authorization: `Bearer ${deviceToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDeviceUser(data.user);
        setCachedSessionToken(deviceToken);
      } else {
        // Token invalid — clear it, user needs to re-login
        clearDeviceToken();
        setDeviceUser(null);
      }
    } catch {
      // Network error — keep existing deviceUser if any
    } finally {
      setIsDevicePending(false);
    }
  }, []);

  // On native: fetch user via device token on mount and when token changes
  useEffect(() => {
    if (!isNative) return;
    fetchDeviceSession();
  }, [isNative, fetchDeviceSession]);

  // Resolve user: web uses session, native uses device token
  const user = isNative ? deviceUser : sessionUser;
  const isAuthenticated = !!user;
  const isPending = isNative ? isDevicePending : isSessionPending;

  // Cache session token for non-React code (API client bearer auth, WS auth)
  useEffect(() => {
    if (sessionToken) {
      setCachedSessionToken(sessionToken);
    }
  }, [sessionToken]);

  // Auto-subscribe to push notifications on login (web)
  useAutoPushSubscription(isNative ? null : user);

  // Native push notifications (iOS/Android) — FCM for cloud
  const nativePushAttempted = useRef(false);
  useEffect(() => {
    if (!user?.id || nativePushAttempted.current || !isNative) return;
    nativePushAttempted.current = true;
    // Request local notification permission first, then init push
    import('@/lib/notifications/local-notification').then(({ requestNotificationPermission }) =>
      requestNotificationPermission(),
    ).finally(() => initNativePushNotifications(user.id));
  }, [user?.id, isNative]);

  // Real-time notification WebSocket (on-prem only)
  useNotificationWebSocket({
    userId: user?.id,
    token: sessionToken,
    enabled: !!user?.id,
  });

  // Background foreground service for Android on-prem (notifications while screen locked)
  useBackgroundWs({
    userId: user?.id,
    token: sessionToken || getDeviceToken(),
    enabled: !!user?.id,
  });

  const setBackendUrl = (url: string) => {
    setBackendUrlFn(url);
  };

  const clearBackendUrl = () => {
    stopBackgroundService();
    clearBackendUrlFn();
  };

  // Set native user directly (called after native login to avoid page reload)
  const setNativeUser = useCallback((userData: AuthUser, token: string) => {
    setDeviceUser(userData);
    setCachedSessionToken(token);
    setIsDevicePending(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isPending,
        backendUrl: getBackendUrl(),
        setBackendUrl,
        clearBackendUrl,
        setNativeUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
